import { request } from '../utils/request';
import type { IncidentReportResponse } from '@/types/incident-report.types';
import { submitIncidentReportOffline } from '../services/offline-surveys.service';

export const createIncidentReport = async (
  formData: FormData,
): Promise<{ message: string; uuid: string }> => {
  // If offline, save to IndexedDB
  if (!navigator.onLine) {
    const description = formData.get('description') as string;
    const images: File[] = [];

    // Extract all image files from FormData (field name is 'photos' from the form)
    formData.getAll('photos').forEach((file) => {
      if (file instanceof File) {
        images.push(file);
      }
    });

    const localId = await submitIncidentReportOffline(description, images);
    return {
      message:
        'Incident report saved offline. It will be submitted when you are back online.',
      uuid: localId,
    };
  }

  // Online: submit to server
  return request<{ message: string; uuid: string }>(
    '/surveys/incident-reports',
    {
      method: 'POST',
      body: formData,
      isFormData: true,
    },
  );
};

export const getIncidentReportByUuid = async (
  uuid: string,
): Promise<IncidentReportResponse> => {
  return request<IncidentReportResponse>(`/surveys/incident-reports/${uuid}`, {
    method: 'GET',
  });
};

export const markIncidentReportAsViewed = async (
  uuid: string,
): Promise<{ message: string }> => {
  return request<{ message: string }>(
    `/surveys/incident-reports/${uuid}/viewed`,
    {
      method: 'PATCH',
    },
  );
};

export const markIncidentReportAsUnviewed = async (
  uuid: string,
): Promise<{ message: string }> => {
  return request<{ message: string }>(
    `/surveys/incident-reports/${uuid}/unviewed`,
    {
      method: 'PATCH',
    },
  );
};

export const downloadIncidentReportPdf = async (
  uuid: string,
): Promise<void> => {
  const { blob, filename } = await request<{
    blob: Blob;
    filename: string | null;
  }>(`/surveys/incident-reports/${uuid}/download`, {
    responseType: 'blob',
    returnFilename: true,
  });

  // Create download link and trigger download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename ?? 'IncidentReport.pdf';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
